///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story authoring helpers: capture-not-type, same philosophy as saved
/// views. "Add stop" copies the current scene (camera pose, visible
/// layers, focused entity) into an embedded StoryStop; the author writes
/// the card text afterwards.
///

import { appStore } from "@/redux/store"
import type { Story, StoryDatasetRef, StoryStop } from "@/redux/storiesSlice"
import { autoNameForCamera } from "@/utils/savedViews"

/** Create an empty draft story ready for "Add stop" */
export function createStory(title = "Untitled story"): Story {
	const now = new Date().toISOString()
	return {
		id: crypto.randomUUID(),
		title,
		stops: [],
		annotations: [],
		datasets: [],
		tags: [],
		createdAt: now,
		updatedAt: now,
	}
}

/** A captured stop plus the cloud dataset refs it depends on (merged
 *  into the story pool by addStop/recaptureStop) */
export interface StoryStopCapture {
	stop: StoryStop
	datasets: StoryDatasetRef[]
}

/**
 * Capture the current scene as a story stop. The stop title seeds from
 * the focused entity's display name or a reverse place lookup (both
 * immediately editable); the body starts empty for the author to write.
 * Open dataset windows with a cloud identity (saved to cloud, or opened
 * from a cloud source) are captured as references the stop mounts on
 * playback.
 */
export async function captureStoryStop(): Promise<StoryStopCapture> {
	const state = appStore.getState()
	const camera = state.earthbrowser.cameraState

	// The ?target= URL param is the canonical "focused entity" signal, so
	// a stop captured while an entity is focused refocuses it on playback
	const focusTarget = new URL(window.location.href).searchParams.get("target")

	// A focused target names the stop after itself (the info window title
	// is its display name); free camera stops get the place lookup
	const infoWindow = state.userInterface.infoWindow
	const focusName = focusTarget && infoWindow.isOpen ? infoWindow.title.trim() : ""

	// Visible layers are recorded as enable-only dependencies, so
	// layer-dependent content exists when the stop plays back
	const visibleDatasets = state.earthbrowser.visibleDatasets

	// Open cloud-linked dataset windows become stop dependencies (refs,
	// not embedded data; local-only imports can't travel with a story).
	// Image overlays capture their live placement so per-story bounds and
	// opacity tweaks travel with the story.
	const datasets: StoryDatasetRef[] = []
	for (const window of Object.values(state.dialog.datasetWindows)) {
		const link = window.cloudDataset
		if (!link || datasets.some(d => d.id === link.id)) continue
		const ref: StoryDatasetRef = { ...link }
		if (link.format === "image" && window.imageOverlay) {
			ref.bounds = { ...window.imageOverlay.bounds }
			ref.opacity = window.imageOverlay.opacity
		}
		datasets.push(ref)
	}

	const stop: StoryStop = {
		id: crypto.randomUUID(),
		camera: { ...camera },
		title: focusName.length > 0 ? focusName : await autoNameForCamera(camera),
		body: "",
		focusTargetId: focusTarget ?? undefined,
		layers: visibleDatasets.length > 0 ? [...visibleDatasets] : undefined,
		dwell: { mode: "hold" },
		datasetIds: datasets.length > 0 ? datasets.map(d => d.id) : undefined,
	}
	return { stop, datasets }
}
